import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { AuthProvider, DevAuthProvider } from '@revalize/auth/frontend'
import { WebStorageStateStore } from 'oidc-client-ts'
import { initializeMonitoring } from '@revalize/monitoring/frontend'
import { App } from './App'
import { ConfigService } from './services/ConfigService'
import { ErrorFallback } from './components/ErrorFallback'
import { initFeatures } from './features/registry'
import { installChunkReloadRecovery } from './lib/chunk-reload'
import { logger } from './utils/logger'
import './index.css'

// Self-heal stale lazy-chunk failures after a deploy (must be installed before
// any dynamic import fires, i.e. before initFeatures / lazy routes).
installChunkReloadRecovery()

// Get or create React root
const rootElement = document.getElementById('root')!
const root = ReactDOM.createRoot(rootElement)

/**
 * Initialize app: load runtime config then render
 */
async function initializeApp(): Promise<void> {
  try {
    // Load runtime config from /api/config (no-op in dev mode)
    await ConfigService.initialize()
    
    // Load enabled feature modules (dynamic imports — disabled features are not loaded)
    await initFeatures()

    // Initialize monitoring with config from backend
    const monitoringConfig = ConfigService.getMonitoringConfig()
    if (monitoringConfig) {
      initializeMonitoring(monitoringConfig)
    }
    
    // Get config for rendering
    const oidcConfig = ConfigService.getOidcConfig()
    const cognitoExtensions = ConfigService.getCognitoExtensions()
    const isDevMode = ConfigService.isDevAuthMode()

    // Persist the OIDC user (incl. refresh token) in localStorage so the session
    // survives a browser/tab close. oidc-client-ts defaults userStore to
    // sessionStorage, which is wiped on close — forcing a full Hosted-UI login (and
    // mandatory MFA) every browser session. With localStorage, the 30-day refresh
    // token + automaticSilentRenew keep the user signed in, so MFA only re-fires when
    // the refresh token actually expires. Spread (not an explicit prop) because
    // AppAuthProviderProps is a union where userStore lives only on one branch.
    const sessionPersistence = {
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    }
    
    // Render app with appropriate auth provider
    root.render(
      <React.StrictMode>
        <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
          {isDevMode ? (
            <DevAuthProvider>
              <App />
            </DevAuthProvider>
          ) : oidcConfig ? (
            <AuthProvider
              {...oidcConfig}
              {...cognitoExtensions}
              {...sessionPersistence}
            >
              <App />
            </AuthProvider>
          ) : (
            <div>Configuration error: OIDC config required</div>
          )}
        </Sentry.ErrorBoundary>
      </React.StrictMode>
    )
  } catch (error) {
    // Fatal config-init failure — the user sees a static error screen and
    // nothing else fires in the app. Log to Sentry before rendering so
    // support at least sees that the boot sequence blew up.
    logger.error(
      'Config init failed — rendering static error screen',
      error instanceof Error ? error : undefined,
    )
    // Render error screen on fatal config failure
    root.render(
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontFamily: 'system-ui'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '600px' }}>
          <h1>Configuration Error</h1>
          <p>{error instanceof Error ? error.message : 'Unknown error'}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    )
  }
}

// Show loading screen while config loads (vanilla HTML, not React)
rootElement.innerHTML = `
  <div style="display:flex;align-items:center;justify-content:center;height:100vh">
    <div style="text-align:center">
      <div style="font-size:24px;margin-bottom:16px">⚙️</div>
      <div style="font-size:16px;color:#666">Loading configuration...</div>
    </div>
  </div>
`

// Start initialization
initializeApp()
